import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    apiCreateChangeOrder,
    apiDeleteChangeOrder,
    apiGetChangeOrder,
    apiGetChangeOrderSummary,
    apiListChangeOrders,
    apiSetChangeOrderStatus,
    apiUpdateChangeOrder,
    type CreateChangeOrderInput,
    type UpdateChangeOrderPatch,
} from '../api/changeOrders.api';

export function useChangeOrders(projectId: string, contractId: string, enabled = true) {
    return useQuery({
        queryKey: ['changeOrders', projectId, contractId],
        queryFn: () => apiListChangeOrders(projectId, contractId),
        enabled: !!projectId && !!contractId && enabled,
    });
}

export function useChangeOrderSummary(projectId: string, contractId: string, enabled = true) {
    return useQuery({
        queryKey: ['changeOrderSummary', projectId, contractId],
        queryFn: () => apiGetChangeOrderSummary(projectId, contractId),
        enabled: !!projectId && !!contractId && enabled,
    });
}

export function useChangeOrder(projectId: string, contractId: string, changeOrderId: string | null, enabled = true) {
    return useQuery({
        queryKey: ['changeOrder', projectId, contractId, changeOrderId],
        queryFn: () => apiGetChangeOrder(projectId, contractId, changeOrderId as string),
        enabled: !!projectId && !!contractId && !!changeOrderId && enabled,
    });
}

export function useCreateChangeOrder(projectId: string, contractId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateChangeOrderInput) => apiCreateChangeOrder(projectId, contractId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['changeOrders', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrderSummary', projectId, contractId] });
        },
    });
}

export function useUpdateChangeOrder(projectId: string, contractId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: { changeOrderId: string; patch: UpdateChangeOrderPatch }) =>
            apiUpdateChangeOrder(projectId, contractId, args.changeOrderId, args.patch),
        onSuccess: (_d, v) => {
            qc.invalidateQueries({ queryKey: ['changeOrders', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrderSummary', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrder', projectId, contractId, v.changeOrderId] });
        },
    });
}

export function useDeleteChangeOrder(projectId: string, contractId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (changeOrderId: string) => apiDeleteChangeOrder(projectId, contractId, changeOrderId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['changeOrders', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrderSummary', projectId, contractId] });
        },
    });
}

export function useSetChangeOrderStatus(projectId: string, contractId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: { changeOrderId: string; body: any }) =>
            apiSetChangeOrderStatus(projectId, contractId, args.changeOrderId, args.body),
        onSuccess: (_d, v) => {
            qc.invalidateQueries({ queryKey: ['changeOrders', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrderSummary', projectId, contractId] });
            qc.invalidateQueries({ queryKey: ['changeOrder', projectId, contractId, v.changeOrderId] });
        },
    });
}
